import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { MotionConfig } from 'motion/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import ErrorBoundary from './components/ErrorBoundary'
import CookieConsent from './components/CookieConsent'
import i18n, { loadLanguage } from './i18n'
import { getPathLang, langBasename } from './lib/lang'
import { registerWebMcpTools } from './lib/webmcp'
import { HydrationProvider } from './lib/hydration'
import './index.css'

// Jazyk MUSÍ odpovídat URL: prefix (/en, /de…) → daný jazyk; BEZ prefixu = čeština.
// Bez fallbacku na 'cs' by se na kořeni / vzal jazyk prohlížeče (detektor navigator) a
// Seznambot/cizí prohlížeč by dostal anglický obsah → Seznam hlásí „stránka není v češtině".
const pathLang = getPathLang() || 'cs'

// <html lang="…"> + content-language drž synchronně s aktivním jazykem (přístupnost + SEO).
const applyHtmlLang = (lng: string) => {
  document.documentElement.lang = lng
  let meta = document.querySelector('meta[http-equiv="content-language"]') as HTMLMetaElement | null
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('http-equiv', 'content-language')
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', lng)
}
applyHtmlLang(pathLang)
i18n.on('languageChanged', applyHtmlLang)

// WebMCP — zpřístupní naše akce AI agentům v prohlížeči (no-op tam, kde API není).
registerWebMcpTools()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

const tree = (
  <StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={langBasename()}>
          {/* reducedMotion="user" → Framer Motion respektuje OS „omezit pohyb" (WCAG) */}
          <MotionConfig reducedMotion="user">
            <HydrationProvider>
              <App />
              <CookieConsent />
            </HydrationProvider>
          </MotionConfig>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>
)

// Nejdřív načti překlady aktivního jazyka (+ cs fallback), pak vykresli. Jazyk odpovídá
// prefixu cesty (= langForRoute v prerenderu), takže klient kreslí stejný jazyk jako snímek.
async function boot() {
  await loadLanguage('cs')
  if (pathLang !== 'cs') await loadLanguage(pathLang)
  if (i18n.language !== pathLang) await i18n.changeLanguage(pathLang)
  createRoot(document.getElementById('root')!).render(tree)
}

// KLÍČOVÉ pro FCP/LCP: createRoot prerenderovaný obsah zahodí a kreslí znovu, a jeho první
// render je pod pomalou sítí/CPU drahý (~2,4 s) → kdyby běžel hned po bundlu, první paint by
// padl až na něj (měřeno: FCP 8,8 s / LCP 15,2 s, přestože byl obsah v HTML). Proto createRoot
// spustíme až PO PRVNÍM PAINTU: prohlížeč vykreslí prerenderovaný obsah (FCP/LCP padnou na něj
// ~hned) a teprve pak React převezme řízení. Díky gatingu animací (HydrationProvider) je
// první render Reactu vizuálně IDENTICKÝ se snímkem → žádný flash, žádný posun (CLS).
// Spouštěč = PerformanceObserver('paint') (vystřelí při FCP); fallbacky (load + tvrdý timeout)
// pokrývají prostředí bez paint eventu (prerender v puppeteeru) — tam React vyrenderuje obsah
// pro snímek.
let started = false
const startOnce = () => { if (started) return; started = true; void boot() }
if (typeof PerformanceObserver === 'function') {
  try {
    const po = new PerformanceObserver(() => { po.disconnect(); startOnce() })
    po.observe({ type: 'paint', buffered: true })
  } catch { /* paint timing nepodporováno → fallbacky níže */ }
}
if (document.readyState === 'complete') setTimeout(startOnce, 0)
else window.addEventListener('load', () => setTimeout(startOnce, 0), { once: true })
setTimeout(startOnce, 2000)
